import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const KEY = "tfl_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const openCart = useCallback(() => setOpen(true), []);
  const closeCart = useCallback(() => setOpen(false), []);

  const addItem = useCallback((line) => {
    setItems((prev) => {
      // configurable items always create a new line; simple items merge by product
      if (!line.configurable) {
        const idx = prev.findIndex((i) => i.product_id === line.product_id && !i.configurable);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + (line.quantity || 1) };
          return copy;
        }
      }
      return [...prev, { ...line, lineId: crypto.randomUUID(), quantity: line.quantity || 1 }];
    });
    setOpen(true);
    toast.success(`${line.nom} ajouté au panier`);
  }, []);

  const updateQty = useCallback((lineId, qty) => {
    setItems((prev) => prev.map((i) => (i.lineId === lineId ? { ...i, quantity: Math.max(1, qty) } : i)));
  }, []);

  const editLine = useCallback((lineId, patch) => {
    setItems((prev) => prev.map((i) => (i.lineId === lineId ? { ...i, ...patch } : i)));
  }, []);

  const removeItem = useCallback((lineId) => {
    setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const reorder = useCallback((orderItems) => {
    const rebuilt = orderItems.map((i) => ({
      ...i, lineId: crypto.randomUUID(), quantity: i.quantity || 1,
    }));
    setItems(rebuilt);
    setOpen(true);
    toast.success("Commande rechargée dans le panier");
  }, []);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.prix * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  return (
    <CartContext.Provider value={{
      items, open, openCart, closeCart, addItem, updateQty, editLine,
      removeItem, clear, reorder, subtotal, count,
    }}>
      {children}
    </CartContext.Provider>
  );
}
